import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import { seo } from "@/lib/seo";

const FARM_IMG = "/assets/images/wholesale-farm.jpg";
const FACILITY_IMG = "/assets/images/wholesale-facility.jpg";
const BLANKLABEL_IMG = "/assets/images/wholesale-blanklabel.jpg";

export const Route = createFileRoute("/wholesale")({
  head: () => ({
    meta: [
      ...seo({
        title: "Wholesale & Private Label — QT Foods",
        description:
          "Direct-from-origin Japanese tea, matcha and teaware for retailers, cafés, hospitality and gifting. Blank-label and private-label programs with transparent MOQs and lead times.",
      }),
    ],
  }),
  component: Page,
});

const STEPS = [
  {
    title: "Browse the catalog",
    body: "Add the teas and teaware you want pricing on to your inquiry list. No login, no commitment.",
  },
  {
    title: "Send your inquiry",
    body: "Share company details, target volumes and any packaging or branding requirements.",
  },
  {
    title: "Receive your quote",
    body: "Our team replies within 1–2 business days with pricing, MOQ, lead times and sample options.",
  },
  {
    title: "Production & shipping",
    body: "We coordinate blank-label production, quality control and international shipping under agreed Incoterms.",
  },
];

const TRUST = [
  {
    k: "Direct sourcing",
    v: "Long-standing relationships with growers in Uji, Nishio, Shizuoka and Kagoshima — no middlemen between farm and freight.",
  },
  {
    k: "Quality assured",
    v: "Every lot is cupped, residue-tested and traceable to the producing region and harvest.",
  },
  {
    k: "Built for buyers",
    v: "Stable supply year-round, consolidated shipments, and one point of contact from sample to container.",
  },
];

function Page() {
  return (
    <SiteLayout breadcrumbs={[{ label: "Wholesale & Private Label" }]}>
      {/* Hero with story */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 pt-10 pb-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pt-14 lg:pb-28">
          <div>
            <h1 className="mt-3 font-serif text-5xl md:text-6xl">
              A quiet partner behind your brand.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
              QT Foods supplies authentic Japanese tea, matcha and teaware to retailers, cafés,
              hospitality groups and corporate gifting programs around the world. We work directly
              with growers and small manufacturers in Japan, and we ship under your label or ours —
              discreetly, consistently, and at the volumes your business actually needs.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/collections"
                className="rounded-sm bg-primary px-6 py-3 text-sm font-medium tracking-wide text-primary-foreground hover:bg-primary/90"
              >
                Browse catalog
              </Link>
              <Link
                to="/contact"
                className="rounded-sm border border-border px-6 py-3 text-sm font-medium tracking-wide text-foreground hover:bg-foreground/5"
              >
                Talk to our team
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={FARM_IMG}
              alt="A Japanese green tea plantation on terraced hills in morning mist"
              className="h-full w-full object-cover"
              width={1024}
              height={1024}
            />
          </div>
        </div>
      </section>

      {/* Origin story */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Our story</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
              From the hillsides of Japan to your shelf.
            </h2>
          </div>
          <div className="space-y-5 text-base leading-relaxed text-foreground/80 lg:col-span-7">
            <p>
              QT Foods was built to do one thing well: move genuinely good Japanese tea and teaware
              from the people who make it to the businesses that sell it. We grew out of years spent
              on the ground in Uji, Nishio, Shizuoka and Kagoshima — visiting farms, cupping
              samples, and learning how a single change in steaming time or shade-growing can
              completely reshape a cup.
            </p>
            <p>
              Today we partner with a small, hand-picked group of growers and processors. Some are
              multi-generation family farms; others are co-operatives and modern facilities that
              have invested in stainless-steel processing, cold storage and rigorous residue
              testing. What unites them is consistency — the ability to deliver the same matcha, the
              same sencha, the same hojicha, harvest after harvest, container after container.
            </p>
            <p>
              That consistency is what your customers feel, even when they can't name it. It's also
              what makes us a useful wholesale partner: predictable supply, honest specifications,
              and a single point of contact from first sample to landed freight.
            </p>
          </div>
        </div>
      </section>

      {/* Quality / facility image with story */}
      <section className="border-y border-border">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
          <div className="order-2 lg:order-1 overflow-hidden rounded-sm">
            <img
              src={FACILITY_IMG}
              alt="Workers inspecting fresh green tea leaves inside a modern Japanese tea processing facility"
              className="h-full w-full object-cover"
              loading="lazy"
              width={1024}
              height={1024}
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Quality</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl">Tested, traceable, on time.</h2>
            <p className="mt-5 text-base leading-relaxed text-foreground/80">
              Every shipment we send out is traceable to the producing region and harvest. Lots are
              cupped on arrival, screened for residues and moisture, and held in temperature- and
              humidity-controlled storage until they're packed for you. We over-document on purpose:
              specifications, certificates of analysis and packing lists travel with every order so
              your QA team — or your private-label customer's — can verify what they're getting.
            </p>
            <dl className="mt-8 space-y-5 border-t border-border pt-6">
              {TRUST.map((t) => (
                <div key={t.k} className="grid gap-1 md:grid-cols-[180px_1fr] md:gap-6">
                  <dt className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                    {t.k}
                  </dt>
                  <dd className="text-sm leading-relaxed text-foreground/80">{t.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Blank label */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-primary">
              Blank label & private label
            </p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
              Your brand on the front. Our work behind it.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-foreground/80">
              Most of our customers don't sell tea under our name — they sell it under theirs. We
              offer the catalog in three formats:
            </p>
            <ul className="mt-6 space-y-4 text-sm leading-relaxed text-foreground/85">
              <li className="border-t border-border pt-4">
                <span className="font-medium">Blank label.</span> Unbranded kraft pouches or white
                tins, ready to be stickered or repacked by you. The fastest way to bring a tea line
                to market.
              </li>
              <li className="border-t border-border pt-4">
                <span className="font-medium">Private label.</span> Your design printed directly on
                pouches, tins, sachets or gift boxes. We coordinate artwork, dielines, regulatory
                text and print proofs before any unit is produced.
              </li>
              <li className="border-t border-border pt-4">
                <span className="font-medium">Bulk.</span> Foodservice-format bags and cases for
                cafés, restaurants and hotel groups that portion in-house.
              </li>
            </ul>
          </div>
          <div className="overflow-hidden rounded-sm">
            <img
              src={BLANKLABEL_IMG}
              alt="Flat lay of unbranded kraft tea pouches and white tins beside a small dish of bright green matcha"
              className="h-full w-full object-cover"
              loading="lazy"
              width={1024}
              height={1024}
            />
          </div>
        </div>
      </section>

      {/* Who we work with */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
          <h2 className="font-serif text-3xl md:text-4xl">Who we work with</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                t: "Specialty retailers",
                b: "Independent tea shops, grocers and lifestyle stores building a curated Japanese tea range.",
              },
              {
                t: "Cafés & roasters",
                b: "Coffee shops and roasteries adding matcha, hojicha and sencha to their bar menu.",
              },
              {
                t: "Hotels & hospitality",
                b: "Hotel groups, restaurants and lounges that want a consistent house tea program.",
              },
              {
                t: "Gifting & corporate",
                b: "Gift companies and brands producing branded sets for clients, employees and events.",
              },
            ].map((c) => (
              <div key={c.t} className="border-t border-border pt-6">
                <h3 className="font-serif text-xl">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <h2 className="font-serif text-3xl md:text-4xl">How it works</h2>
        <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="border-t border-border pt-6">
              <span className="text-xs uppercase tracking-[0.2em] text-primary">Step {i + 1}</span>
              <h3 className="mt-2 font-serif text-xl">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Terms */}
      <section className="border-t border-border">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:grid-cols-3 lg:px-8">
          <Spec
            title="MOQ"
            body="Teas from 1–5 kg per SKU. Teaware MOQs vary by item. Custom-printed packaging typically requires 200–500 units."
          />
          <Spec
            title="Lead times"
            body="In-stock items ship within 1–2 weeks. Private-label production runs 4–8 weeks depending on packaging complexity."
          />
          <Spec
            title="Shipping"
            body="Worldwide by sea and air freight. Incoterms, documentation and consolidation confirmed at quote."
          />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-5 py-20 text-center lg:px-8 lg:py-24">
          <h2 className="font-serif text-3xl md:text-5xl">Start your inquiry.</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-foreground/80">
            Add the items you're interested in and send them to our wholesale team. We'll come back
            with pricing, MOQ and lead times tailored to your project.
          </p>
          <Link
            to="/collections"
            className="mt-8 inline-block rounded-sm bg-primary px-7 py-3 text-sm font-medium tracking-wide text-primary-foreground hover:bg-primary/90"
          >
            Browse catalog
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}

function Spec({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{title}</h3>
      <p className="mt-3 font-serif text-2xl leading-snug">{body}</p>
    </div>
  );
}
